<template>
  <v-dialog v-model="isOpen" max-width="640" scrollable @click:outside="close">
    <v-card v-if="loading" class="pa-8 text-center">
      <v-progress-circular indeterminate size="40" color="accent" />
      <div class="text-body-2 text-grey mt-3">Loading customer...</div>
    </v-card>

    <v-card v-else-if="error" class="pa-8 text-center">
      <v-icon color="error" size="40">mdi-alert-circle</v-icon>
      <div class="text-body-1 mt-3">{{ error }}</div>
      <v-btn variant="text" class="mt-4" @click="close">Close</v-btn>
    </v-card>

    <v-card v-else-if="customer">
      <!-- Header -->
      <div class="customer-card-header d-flex align-center pa-3">
        <v-avatar size="36" :color="customer.customer_type === 'Commercial' ? 'blue' : 'green'" class="mr-3">
          <v-icon color="white" size="20">
            {{ customer.customer_type === 'Commercial' ? 'mdi-domain' : 'mdi-home-account' }}
          </v-icon>
        </v-avatar>
        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-subtitle-1 font-weight-bold text-truncate" style="color: #1e3a5f;">
            {{ customer.name }}
          </div>
          <div class="d-flex align-center ga-2">
            <v-chip
              :color="customer.is_active ? 'success' : 'grey'"
              size="x-small"
              variant="tonal"
            >
              {{ customer.is_active ? 'Active' : 'Inactive' }}
            </v-chip>
            <v-chip v-if="customer.customer_type" size="x-small" variant="outlined">
              {{ customer.customer_type }}
            </v-chip>
            <v-chip v-if="customer.do_not_service" color="error" size="x-small" variant="tonal" prepend-icon="mdi-block-helper">
              Do Not Service
            </v-chip>
            <!--
              Notes counter chip — shows when there are user-authored notes
              (incl. the migrated legacy `customer.notes` value, if any).
              Click jumps the body scroll down to the Notes section.
            -->
            <v-chip
              v-if="notesCount > 0"
              color="primary"
              size="x-small"
              variant="tonal"
              prepend-icon="mdi-note-text-outline"
              class="cursor-pointer"
              title="Jump to notes"
              @click="scrollToNotes"
            >
              Notes ({{ notesCount }})
            </v-chip>
          </div>
        </div>
        <v-btn
          v-if="canViewFullProfile && customer.id"
          icon
          size="small"
          variant="tonal"
          color="primary"
          class="mr-1"
          :title="t('customer_card_dialog.open_full_profile')"
          @click="openFullProfile"
        >
          <v-icon size="18">mdi-account-details</v-icon>
        </v-btn>
        <v-btn
          icon
          size="small"
          variant="tonal"
          color="primary"
          class="mr-1"
          title="Edit Customer"
          @click="openEdit"
        >
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon size="small" variant="text" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="pa-0" style="max-height: 80vh; overflow-y: auto;">
        <!-- Quick Actions -->
        <div class="d-flex pa-3 ga-2 flex-wrap" style="background: #f8f9fb;">
          <v-btn
            v-if="primaryPhone"
            size="small"
            variant="tonal"
            color="success"
            prepend-icon="mdi-phone"
            @click="dialPhone(primaryPhone)"
          >
            Call
          </v-btn>
          <v-btn
            v-if="primaryPhone"
            size="small"
            variant="tonal"
            color="info"
            prepend-icon="mdi-message-text"
            @click="openSms(primaryPhone)"
          >
            SMS
          </v-btn>
          <v-btn
            v-if="primaryEmail"
            size="small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-email"
            @click="openEmail(primaryEmail)"
          >
            Email
          </v-btn>
          <v-btn
            v-if="primaryAddress"
            size="small"
            variant="tonal"
            color="warning"
            prepend-icon="mdi-map-marker"
            @click="openMap(primaryAddress)"
          >
            Map
          </v-btn>
        </div>

        <!-- Contacts -->
        <div v-if="customer.contacts && customer.contacts.length" class="px-3 pt-3 pb-1">
          <div class="text-overline text-medium-emphasis mb-1">Contacts</div>
          <div
            v-for="contact in customer.contacts"
            :key="contact.id"
            class="d-flex align-center py-1"
          >
            <v-icon
              size="16"
              class="mr-2"
              :color="contactIconColor(contact.contact_type)"
            >
              {{ contactIcon(contact.contact_type) }}
            </v-icon>
            <span
              class="text-body-2 font-weight-medium contact-value"
              :class="isClickableContact(contact) ? 'clickable-link' : ''"
              @click="onContactClick(contact)"
            >
              {{ contact.value }}
            </span>
            <v-chip v-if="contact.label" size="x-small" variant="tonal" color="primary" class="ml-1">
              {{ contact.label }}
            </v-chip>
            <v-chip v-if="contact.is_primary" size="x-small" variant="text" color="accent" class="ml-1">
              primary
            </v-chip>
            <span class="text-caption text-medium-emphasis ml-1">{{ contact.contact_type }}</span>
            <!-- Action icons for each contact -->
            <v-spacer />
            <template v-if="contact.contact_type === 'Phone' || contact.contact_type === 'MobilePhone'">
              <v-btn icon size="x-small" variant="text" color="success" title="Call" @click="dialPhone(contact.value)">
                <v-icon size="14">mdi-phone</v-icon>
              </v-btn>
              <v-btn icon size="x-small" variant="text" color="info" title="SMS" @click="openSms(contact.value)">
                <v-icon size="14">mdi-message-text</v-icon>
              </v-btn>
            </template>
            <template v-if="contact.contact_type === 'Email'">
              <v-btn icon size="x-small" variant="text" color="primary" title="Email" @click="openEmail(contact.value)">
                <v-icon size="14">mdi-email-send</v-icon>
              </v-btn>
            </template>
          </div>
        </div>

        <v-divider v-if="customer.contacts && customer.contacts.length" class="mx-3" />

        <!-- Locations with Jobs -->
        <div v-if="locationsWithJobs.length || (customer.locations && customer.locations.length)" class="px-3 pt-3 pb-1">
          <div class="text-overline text-medium-emphasis mb-1">
            Locations & Jobs
            <v-progress-circular v-if="locJobsLoading" indeterminate size="12" width="2" class="ml-1" />
          </div>
          <div
            v-for="loc in (locationsWithJobs.length ? locationsWithJobs : customer.locations)"
            :key="loc.id || 'no-loc'"
            class="mb-2"
          >
            <div class="d-flex align-center py-1">
              <v-icon size="16" class="mr-2" :color="loc.is_active !== false ? 'success' : 'grey'">mdi-map-marker</v-icon>
              <div class="flex-grow-1" style="min-width: 0;">
                <div v-if="loc.name && loc.name !== 'Location'" class="text-body-2 font-weight-medium">{{ loc.name }}</div>
                <a
                  :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(loc.full_address || buildAddress(loc))"
                  target="_blank"
                  class="text-caption text-decoration-none clickable-link"
                  @click.stop
                >
                  {{ loc.full_address || buildAddress(loc) }}
                  <v-icon size="10" class="ml-1">mdi-open-in-new</v-icon>
                </a>
              </div>
              <v-chip v-if="loc.jobs_count" size="x-small" color="accent" variant="tonal" class="ml-1">
                {{ loc.jobs_count }} job{{ loc.jobs_count !== 1 ? 's' : '' }}
              </v-chip>
            </div>
            <!-- Jobs under location -->
            <div v-if="loc.jobs && loc.jobs.length" class="ml-6 mt-1">
              <div
                v-for="j in loc.jobs.slice(0, expandedLocs[loc.id] ? undefined : 3)"
                :key="j.id"
                class="job-row py-1"
              >
                <a
                  :href="`/jobs/${j.id}`"
                  class="d-flex align-center text-body-2 clickable-link text-decoration-none"
                  style="cursor: pointer; color: inherit;"
                  title="Click to open · Ctrl/Cmd+Click or middle-click to open in new tab"
                  @click.exact.prevent="goToJob(j.id)"
                >
                  <v-chip
                    size="x-small"
                    :color="j.status === 'Completed' ? 'success' : j.status === 'Canceled' ? 'error' : 'warning'"
                    variant="tonal"
                    class="mr-2"
                    style="min-width: 70px; justify-content: center;"
                  >
                    {{ j.status }}
                  </v-chip>
                  <span class="font-weight-medium mr-2">#{{ j.job_number }}</span>
                  <span class="font-weight-medium">{{ formatJobScheduled(j) }}</span>
                  <span v-if="j.technician_name" class="ml-1">· {{ j.technician_name }}</span>
                </a>
                <div
                  v-if="(j.summary || j.job_type)"
                  class="d-flex align-start ml-6"
                  style="gap: 4px; min-width: 0;"
                >
                  <div
                    class="text-caption text-medium-emphasis flex-grow-1"
                    :class="{ 'job-summary-clamp': !expandedJobSummaries[j.id] }"
                    :style="expandedJobSummaries[j.id] ? 'white-space: pre-wrap; word-break: break-word;' : ''"
                  >
                    {{ j.summary || j.job_type }}
                  </div>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    density="compact"
                    :title="expandedJobSummaries[j.id] ? 'Collapse description' : 'Expand description'"
                    @click.stop.prevent="expandedJobSummaries[j.id] = !expandedJobSummaries[j.id]"
                  >
                    <v-icon size="16">
                      {{ expandedJobSummaries[j.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>
              <v-btn
                v-if="loc.jobs.length > 3 && !expandedLocs[loc.id]"
                variant="text"
                size="x-small"
                color="accent"
                class="ml-0 mt-1"
                @click="expandedLocs[loc.id] = true"
              >
                Show all {{ loc.jobs.length }} jobs
              </v-btn>
            </div>
          </div>
        </div>

        <v-divider v-if="locationsWithJobs.length || (customer.locations && customer.locations.length)" class="mx-3" />

        <!-- Billing & Roles -->
        <div v-if="hasBillingInfo" class="px-3 pt-3 pb-1">
          <div class="text-overline text-medium-emphasis mb-1">Billing &amp; Roles</div>
          <v-row dense>
            <v-col v-if="customer.bill_to_type" cols="6">
              <div class="text-caption text-medium-emphasis">Bill To</div>
              <v-chip
                :color="billToMeta.color"
                size="small"
                variant="tonal"
                :prepend-icon="billToMeta.icon"
                class="mt-1"
              >
                {{ billToMeta.label }}
              </v-chip>
              <div
                v-if="customer.default_billing_customer_name && customer.bill_to_type !== 'Self'"
                class="text-caption mt-1"
                style="line-height: 1.3;"
              >
                pays:
                <span
                  class="font-weight-medium clickable-link"
                  :title="'Open payer card'"
                  @click="goToPayer"
                >
                  {{ customer.default_billing_customer_name }}
                </span>
              </div>
            </v-col>
            <v-col v-if="paymentTermsDisplay" cols="6">
              <div class="text-caption text-medium-emphasis">Payment Terms</div>
              <div class="text-body-2 font-weight-medium">
                {{ paymentTermsDisplay }}
              </div>
              <div
                v-if="paymentTermsSource"
                class="text-caption text-medium-emphasis"
                style="line-height: 1.2;"
              >
                inherited from {{ paymentTermsSource }}
              </div>
            </v-col>
            <v-col v-if="roleChips.length" cols="12" class="mt-1">
              <div class="text-caption text-medium-emphasis">Roles</div>
              <div class="d-flex flex-wrap ga-1 mt-1">
                <v-chip
                  v-for="r in roleChips"
                  :key="r.code"
                  :color="r.color"
                  size="x-small"
                  variant="tonal"
                  :prepend-icon="r.icon"
                >
                  {{ r.label }}
                </v-chip>
              </div>
            </v-col>
            <!-- Tenants this customer pays for (Landlord / PM / Warranty) -->
            <v-col v-if="customer.pays_for && customer.pays_for.length" cols="12" class="mt-1">
              <div class="text-caption text-medium-emphasis">
                Pays for ({{ customer.pays_for.length }})
              </div>
              <div class="d-flex flex-column ga-1 mt-1">
                <div
                  v-for="t in customer.pays_for"
                  :key="t.id"
                  class="text-caption d-flex align-center"
                >
                  <v-icon size="14" class="mr-1" color="grey">mdi-home-account</v-icon>
                  <span
                    class="font-weight-medium clickable-link"
                    :title="'Open tenant card'"
                    @click="goToTenant(t.id)"
                  >
                    {{ t.name }}
                  </span>
                  <span v-if="t.address" class="text-medium-emphasis ml-1">— {{ t.address }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider v-if="hasBillingInfo" class="mx-3" />

        <!-- Payments History -->
        <div class="px-3 pt-3 pb-1">
          <div class="text-overline text-medium-emphasis mb-1 d-flex align-center">
            <span>Payments from customer</span>
            <v-progress-circular v-if="balanceLoading" indeterminate size="12" width="2" class="ml-1" />
            <v-spacer />
            <span v-if="paymentsList.length" class="text-caption text-medium-emphasis">
              {{ paymentsList.length }} total
            </span>
          </div>

          <div
            v-if="!balanceLoading && paymentsList.length === 0"
            class="text-body-2 text-medium-emphasis pa-2 rounded text-center"
            style="background: #f5f7fa;"
          >
            <v-icon size="20" color="grey" class="mr-1">mdi-cash-off</v-icon>
            No payments from this customer yet
          </div>

          <div v-else>
            <div
              v-for="p in displayedPayments"
              :key="p.id"
              class="d-flex align-center py-1 px-2 rounded payment-row"
            >
              <v-icon size="16" :color="paymentIconColor(p)" class="mr-2">
                {{ paymentIcon(p) }}
              </v-icon>
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="d-flex align-center ga-1 flex-wrap">
                  <span class="text-body-2 font-weight-bold text-success">
                    ${{ formatNumber(p.amount) }}
                  </span>
                  <span class="text-caption text-medium-emphasis">
                    {{ paymentLabel(p) }}
                  </span>
                  <span
                    v-if="p.job_id"
                    class="text-caption clickable-link"
                    @click="goToJob(p.job_id)"
                  >
                    · #{{ p.invoice_number || p.job_number }}
                  </span>
                </div>
                <div class="text-caption text-medium-emphasis text-truncate" style="line-height: 1.2;">
                  {{ formatPaidAt(p.paid_at) }}
                  <span v-if="p.created_by_name" class="ml-1">· by {{ p.created_by_name }}</span>
                  <span v-if="p.memo" class="ml-1">· {{ p.memo }}</span>
                </div>
              </div>
            </div>
            <div v-if="paymentsList.length > paymentsCollapsedCount" class="text-center mt-1">
              <v-btn
                v-if="!showAllPayments"
                variant="text"
                size="x-small"
                color="accent"
                @click="showAllPayments = true"
              >
                Show all {{ paymentsList.length }} payments
              </v-btn>
              <v-btn
                v-else
                variant="text"
                size="x-small"
                color="accent"
                @click="showAllPayments = false"
              >
                Collapse
              </v-btn>
            </div>
          </div>
        </div>

        <v-divider class="mx-3" />

        <!-- Financial & Info -->
        <div class="px-3 pt-3 pb-2">
          <div class="text-overline text-medium-emphasis mb-1">Details</div>
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption text-medium-emphasis d-flex align-center ga-1">
                <span>Balance</span>
                <v-progress-circular
                  v-if="balanceLoading"
                  indeterminate
                  size="10"
                  width="2"
                  color="grey"
                />
                <v-tooltip text="Refresh balance" location="top">
                  <template #activator="{ props: tipProps }">
                    <v-btn
                      v-bind="tipProps"
                      v-if="!balanceLoading && liveBalance"
                      icon
                      size="x-small"
                      variant="text"
                      @click="refreshBalance"
                    >
                      <v-icon size="12">mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
              <div
                class="text-body-1 font-weight-bold"
                :class="balanceColorClass"
                :title="balanceTooltip"
              >
                {{ balanceDisplay }}
              </div>
              <div v-if="balanceDirectionLabel" class="text-caption font-weight-medium" :class="balanceColorClass" style="line-height: 1.2;">
                {{ balanceDirectionLabel }}
              </div>
              <div v-if="balanceAsOfDisplay" class="text-caption text-medium-emphasis" style="line-height: 1.2;">
                as of {{ balanceAsOfDisplay }}
              </div>
              <div v-if="hasThirdPartyBilled" class="text-caption text-medium-emphasis d-flex align-center mt-1" style="line-height: 1.2;">
                <v-icon size="12" class="mr-1">mdi-shield-check-outline</v-icon>
                {{ thirdPartyDisplay }} billed to {{ thirdPartyPayerLabel }} (not customer's debt)
              </div>
            </v-col>
            <v-col cols="6" v-if="customer.credit_limit">
              <div class="text-caption text-medium-emphasis">Credit Limit</div>
              <div class="text-body-2 font-weight-medium">${{ formatNumber(customer.credit_limit) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text-medium-emphasis">Tax Exempt</div>
              <div class="text-body-2">{{ customer.tax_exempt ? 'Yes' : 'No' }}</div>
            </v-col>
          </v-row>

          <!-- Flags -->
          <div v-if="customer.do_not_mail || customer.do_not_service || customer.national_account" class="d-flex flex-wrap ga-1 mt-2">
            <v-chip v-if="customer.do_not_mail" color="warning" size="x-small" prepend-icon="mdi-email-off">Do Not Mail</v-chip>
            <v-chip v-if="customer.do_not_service" color="error" size="x-small" prepend-icon="mdi-block-helper">Do Not Service</v-chip>
            <v-chip v-if="customer.national_account" color="info" size="x-small" prepend-icon="mdi-star">National Account</v-chip>
          </div>
        </div>

        <v-divider class="mx-3" />

        <!--
          Notes — full section with author, timestamp, attachments and an
          editor (separate dialog) for create/edit/delete. Replaces the
          old inline `customer.notes` text block (legacy `crm_customers.notes`
          is migrated to crm_customer_notes as the first "System" note).
        -->
        <div v-if="customer.id" ref="notesAnchorRef" class="px-3 pt-3 pb-2">
          <CustomerNotesSection
            :customer-id="customer.id"
            @count-changed="onNotesCountChanged"
          />
        </div>

        <v-divider class="mx-3" />

        <!-- Portal Messages -->
        <div class="px-3 pt-3 pb-2">
          <div class="text-overline text-medium-emphasis mb-1">
            Portal Messages
            <v-progress-circular v-if="portalMsgLoading" indeterminate size="12" width="2" class="ml-1" />
          </div>
          <div v-if="!portalMsgLoading && portalMessages.length === 0" class="text-body-2 text-medium-emphasis">
            No portal messages yet
          </div>
          <div v-for="msg in portalMessages" :key="msg.id" class="mb-2 pa-2 rounded" style="background: #f5f7fa;">
            <div class="d-flex align-center ga-2 mb-1">
              <v-chip
                size="x-small"
                :color="msg.tags === 'callback_request' ? 'warning' : msg.tags === 'portal_feedback' ? 'success' : 'info'"
                variant="tonal"
              >
                {{ msg.tags === 'callback_request' ? 'Callback' : msg.tags === 'portal_feedback' ? 'Feedback' : 'Message' }}
              </v-chip>
              <v-chip v-if="msg.overall_rating" size="x-small" color="amber" variant="tonal">
                {{ '★'.repeat(msg.overall_rating) }}{{ '☆'.repeat(5 - msg.overall_rating) }}
              </v-chip>
              <span class="text-caption text-medium-emphasis ml-auto">{{ msg.created_at }}</span>
            </div>
            <div class="text-body-2">{{ msg.comment }}</div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Customer Edit Dialog -->
    <CustomerEditDialog
      v-model="showEdit"
      :customer-id="customer?.id"
      @updated="onCustomerUpdated"
    />

    <!-- Email Modal -->
    <v-dialog v-model="showEmailModal" max-width="500">
      <v-card>
        <v-card-title class="text-subtitle-1 d-flex align-center">
          <v-icon size="20" class="mr-2" color="primary">mdi-email-send</v-icon>
          Send Email
        </v-card-title>
        <v-card-text class="pa-3">
          <v-select
            v-if="fromMailboxItems.length || mailboxesLoading"
            v-model="emailFromResourceId"
            :items="fromMailboxItems"
            :loading="mailboxesLoading"
            label="From"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-at"
            class="mb-2"
            hint="IT Resources + Company Settings mailboxes. Customer replies go here."
            persistent-hint
          />
          <v-text-field :model-value="emailTo" label="To" variant="outlined" density="compact" readonly class="mb-2" />
          <v-text-field v-model="emailSubject" label="Subject" variant="outlined" density="compact" class="mb-2" />
          <v-textarea v-model="emailBody" label="Message" variant="outlined" density="compact" rows="5" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="emailSending" @click="showEmailModal = false">Cancel</v-btn>
          <v-btn color="accent" :loading="emailSending" @click="sendEmail">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SMS Modal — shared single component (chat-style, one send path) -->
    <SmsDialog
      v-model="showSmsModal"
      :customer-id="customer?.id"
      :customer-name="customer?.name || ''"
      :phones="smsPhones"
    />
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useCustomerCardDialog } from '@/composables/useCustomerCardDialog'
import { useAuthStore } from '@/stores/auth'
import { useNotify } from '@/composables/useNotify'
import api from '@/api/client'
import CustomerEditDialog from '@/components/CustomerEditDialog.vue'
import CustomerNotesSection from '@/components/CustomerNotesSection.vue'
import { communicationsApi } from '@/api/communications'
const SmsDialog = defineAsyncComponent(() => import('@/components/communications/SmsDialog.vue'))

const router = useRouter()
const { t } = useI18n()
const { isOpen, customer, loading, error, open, close } = useCustomerCardDialog()
const authStore = useAuthStore()
const { notify } = useNotify()

const showEdit = ref(false)
const locationsWithJobs = ref([])
const locJobsLoading = ref(false)
const expandedLocs = reactive({})
const expandedJobSummaries = reactive({})
const portalMessages = ref([])
const portalMsgLoading = ref(false)

// Notes counter (header chip) and a ref-anchor used by `scrollToNotes()`
// to jump the inner v-card-text scroll container down to the Notes block.
const notesCount = ref(0)
const notesAnchorRef = ref(null)
function onNotesCountChanged(n) {
  notesCount.value = Number(n) || 0
}
function scrollToNotes() {
  // Wait one tick so the DOM exists if the body is rendering.
  setTimeout(() => {
    notesAnchorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, 50)
}

const liveBalance = ref(null)
const balanceLoading = ref(false)
const balanceAsOfTick = ref(0)
const showAllPayments = ref(false)
const paymentsCollapsedCount = 5

let balanceClockInterval = null

const paymentsList = computed(() => liveBalance.value?.payments || [])
const displayedPayments = computed(() =>
  showAllPayments.value ? paymentsList.value : paymentsList.value.slice(0, paymentsCollapsedCount)
)

const PAYMENT_TYPE_LABELS = {
  CreditCard: 'Credit Card',
  Online: 'E-Transfer',
  PMWork: 'PM Work',
  WarrantyWork: 'Warranty Work',
  FreeCheckup: 'Free Checkup',
  Cash: 'Cash',
  Check: 'Check',
  ACH: 'ACH / Bank Transfer',
  PaymentLink: 'Payment Link',
  StoredCard: 'Stored Card',
  Venmo: 'Venmo',
  Zelle: 'Zelle',
  Financing: 'Financing',
  CustomerCredit: 'Customer Credit',
}

function paymentLabel(p) {
  const base = PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type || 'Payment'
  if (p.payment_type === 'CreditCard' && p.card_last_four) {
    const brand = p.card_brand ? `${p.card_brand} ` : ''
    return `${base} (${brand}••• ${p.card_last_four})`
  }
  if (p.payment_type === 'Check' && p.check_number) {
    return `${base} #${p.check_number}`
  }
  return base
}

function paymentIcon(p) {
  const t = p.payment_type
  if (t === 'CreditCard' || t === 'StoredCard' || t === 'PaymentLink') return 'mdi-credit-card-outline'
  if (t === 'Cash') return 'mdi-cash'
  if (t === 'Check') return 'mdi-checkbook'
  if (t === 'Online' || t === 'ACH') return 'mdi-bank-transfer'
  if (t === 'Financing') return 'mdi-finance'
  if (t === 'CustomerCredit') return 'mdi-account-cash-outline'
  if (t === 'WarrantyWork' || t === 'FreeCheckup' || t === 'PMWork') return 'mdi-handshake-outline'
  return 'mdi-cash-multiple'
}

function paymentIconColor(p) {
  if (['WarrantyWork', 'FreeCheckup', 'PMWork'].includes(p.payment_type)) return 'grey'
  return 'success'
}

function formatPaidAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Format a job's scheduled date (DATE only, no time) in the viewer's locale.
 * Falls back to created_at when scheduled_date is missing.
 * Returns "Apr 11, 2026" style strings — short, deterministic, no surprises
 * with locale-dependent "дд.мм.рррр" formats.
 */
function formatJobScheduled(j) {
  const raw = j?.scheduled_date || j?.created_at
  if (!raw) return ''
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw)
  const d = isoDateOnly ? new Date(raw + 'T00:00:00') : new Date(raw)
  if (isNaN(d.getTime())) return raw
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
  })
}

async function loadLiveBalance(customerId) {
  if (!customerId) {
    liveBalance.value = null
    return
  }
  balanceLoading.value = true
  try {
    const { data } = await api.get(`/jobs/customers/${customerId}/live-balance/`)
    liveBalance.value = data
    console.log('[CustomerCard] Live balance loaded:', data)
  } catch (e) {
    console.error('[CustomerCard] Failed to load live balance:', e)
    liveBalance.value = null
  } finally {
    balanceLoading.value = false
  }
}

function refreshBalance() {
  if (customer.value?.id) loadLiveBalance(customer.value.id)
}

const balanceDue = computed(() => {
  if (liveBalance.value && liveBalance.value.balance_due != null) {
    return Number(liveBalance.value.balance_due) || 0
  }
  return Number(customer.value?.balance || 0)
})

const balanceColorClass = computed(() => {
  const v = balanceDue.value
  if (v > 0.005) return 'text-error'      // customer owes us
  if (v < -0.005) return 'text-success'   // we owe customer
  return 'text-medium-emphasis'
})

const balanceDisplay = computed(() => {
  const v = balanceDue.value
  if (v > 0.005) return `-$${formatNumber(Math.abs(v))}`
  if (v < -0.005) return `+$${formatNumber(Math.abs(v))}`
  return `$${formatNumber(0)}`
})

// Plain-language "who owes whom" so it's unmistakable at a glance.
const balanceDirectionLabel = computed(() => {
  const dir = liveBalance.value?.direction || (
    balanceDue.value > 0.005 ? 'customer_owes'
      : (balanceDue.value < -0.005 ? 'we_owe' : 'settled')
  )
  if (dir === 'customer_owes') return t('customer_balance.customer_owes_label')
  if (dir === 'we_owe') return t('customer_balance.we_owe_label')
  return t('customer_balance.settled')
})

// Third-party billing (GE warranty / PM / landlord) — not the customer's debt.
const hasThirdPartyBilled = computed(() =>
  Number(liveBalance.value?.third_party_billed || 0) > 0.009,
)
const thirdPartyDisplay = computed(() =>
  `$${formatNumber(Math.abs(Number(liveBalance.value?.third_party_billed || 0)))}`,
)
const thirdPartyPayerLabel = computed(() => {
  const payers = liveBalance.value?.third_party_payers || []
  if (payers.length === 1) return payers[0].payer_name || 'third party'
  if (payers.length > 1) return 'multiple parties'
  return 'third party'
})

const balanceTooltip = computed(() => {
  if (!liveBalance.value) return ''
  const lb = liveBalance.value
  return `Billed: $${formatNumber(lb.billed)} · Paid: $${formatNumber(lb.paid)}` +
    (lb.adjustments_total ? ` · Adjustments: $${formatNumber(lb.adjustments_total)}` : '') +
    ` · Invoices: ${lb.invoices_count}`
})

const balanceAsOfDisplay = computed(() => {
  // Re-evaluate every minute via balanceAsOfTick so the timestamp stays "live".
  // eslint-disable-next-line no-unused-expressions
  balanceAsOfTick.value
  if (!liveBalance.value?.as_of) return ''
  const d = new Date(liveBalance.value.as_of)
  if (isNaN(d.getTime())) return ''
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  const dateStr = d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  return tz ? `${dateStr} (${tz})` : dateStr
})

watch(() => customer.value?.id, async (newId) => {
  if (!newId) {
    locationsWithJobs.value = []
    portalMessages.value = []
    liveBalance.value = null
    showAllPayments.value = false
    return
  }
  locJobsLoading.value = true
  portalMsgLoading.value = true
  showAllPayments.value = false
  loadLiveBalance(newId)
  try {
    const { data } = await api.get(`/jobs/customers/${newId}/locations-with-jobs/`)
    locationsWithJobs.value = data
  } catch (e) {
    console.error('[CustomerCard] Failed to load locations-with-jobs:', e)
    locationsWithJobs.value = []
  } finally {
    locJobsLoading.value = false
  }
  try {
    const { data } = await api.get(`/portal/admin/customers/${newId}/messages/`)
    portalMessages.value = data.messages || data
    console.log('[CustomerCard] Portal messages loaded:', portalMessages.value.length, 'items')
  } catch (e) {
    console.error('[CustomerCard] Failed to load portal messages:', e)
    portalMessages.value = []
  } finally {
    portalMsgLoading.value = false
  }
}, { immediate: true })

watch(isOpen, (open) => {
  if (open) {
    if (!balanceClockInterval) {
      balanceClockInterval = setInterval(() => { balanceAsOfTick.value++ }, 60_000)
    }
  } else {
    if (balanceClockInterval) {
      clearInterval(balanceClockInterval)
      balanceClockInterval = null
    }
  }
})

function goToJob(jobId) {
  close()
  router.push({ name: 'JobDetail', params: { id: jobId } })
}
const showEmailModal = ref(false)
const showSmsModal = ref(false)
const emailTo = ref('')
const emailSubject = ref('')
const emailBody = ref('')
const emailSending = ref(false)
const emailFromResourceId = ref(null)
const outboundMailboxes = ref([])
const mailboxesLoading = ref(false)

const fromMailboxItems = computed(() =>
  outboundMailboxes.value.map((m) => ({
    title: m.name && m.name !== m.email ? `${m.email} — ${m.name}` : (m.email || m.name),
    value: m.resource_id,
  })),
)

const selectedFromMailbox = computed(() =>
  outboundMailboxes.value.find(m => m.resource_id === emailFromResourceId.value) || null,
)

async function loadOutboundMailboxes() {
  mailboxesLoading.value = true
  try {
    const res = await communicationsApi.getOutboundMailboxes()
    outboundMailboxes.value = res.data?.mailboxes || []
    const defId = res.data?.default_resource_id
    const defEmail = (res.data?.default_from || '').trim()
    const ids = outboundMailboxes.value.map((m) => m.resource_id).filter((id) => id != null)
    if (defId != null && ids.includes(defId)) {
      emailFromResourceId.value = defId
    } else if (defEmail) {
      const match = outboundMailboxes.value.find(
        (m) => (m.email || '').toLowerCase() === defEmail.toLowerCase(),
      )
      if (match?.resource_id != null) {
        emailFromResourceId.value = match.resource_id
      } else {
        const companyName = (authStore.domain?.name || '').trim()
        outboundMailboxes.value = [
          { email: defEmail, name: companyName, resource_id: defId ?? -1 },
          ...outboundMailboxes.value,
        ]
        emailFromResourceId.value = defId ?? -1
      }
    } else if (ids.length) {
      emailFromResourceId.value = ids[0]
    } else {
      emailFromResourceId.value = null
    }
  } catch (err) {
    console.error('[CustomerCardDialog] outbound mailboxes load failed:', err)
    outboundMailboxes.value = []
    emailFromResourceId.value = null
  } finally {
    mailboxesLoading.value = false
  }
}
// SMS sending is handled by the shared <SmsDialog> (single send path ->
// communicationsApi.sendSms -> /crm/comm/send-sms/). We only feed it the
// customer's phone contacts.
const smsPhones = computed(() =>
  (customer.value?.contacts || [])
    .filter(c => /phone/i.test(c.contact_type || ''))
    .map((c, i) => ({
      id: c.id ?? `ccd-ph-${i}`,
      value: c.value,
      is_primary: !!c.is_primary,
      type: c.contact_type || 'Phone',
    })),
)

const primaryPhone = computed(() => {
  if (!customer.value?.contacts) return null
  const phone = customer.value.contacts.find(c =>
    (c.contact_type === 'Phone' || c.contact_type === 'MobilePhone') && c.is_primary
  ) || customer.value.contacts.find(c =>
    c.contact_type === 'Phone' || c.contact_type === 'MobilePhone'
  )
  return phone?.value || null
})

const primaryEmail = computed(() => {
  if (!customer.value?.contacts) return null
  const email = customer.value.contacts.find(c => c.contact_type === 'Email' && c.is_primary)
    || customer.value.contacts.find(c => c.contact_type === 'Email')
  return email?.value || null
})

const primaryAddress = computed(() => {
  if (!customer.value?.locations?.length) {
    if (customer.value?.address_street) {
      return [customer.value.address_street, customer.value.address_city, customer.value.address_state, customer.value.address_zip]
        .filter(Boolean).join(', ')
    }
    return null
  }
  const loc = customer.value.locations[0]
  return loc.full_address || buildAddress(loc)
})

function buildAddress(loc) {
  return [loc.address_street, loc.address_city, loc.address_state, loc.address_zip].filter(Boolean).join(', ')
}

function contactIcon(type) {
  if (type === 'Phone') return 'mdi-phone'
  if (type === 'MobilePhone') return 'mdi-cellphone'
  if (type === 'Email') return 'mdi-email'
  if (type === 'Fax') return 'mdi-fax'
  return 'mdi-card-account-details'
}

function contactIconColor(type) {
  if (type === 'Phone' || type === 'MobilePhone') return 'success'
  if (type === 'Email') return 'info'
  return 'grey'
}

function isClickableContact(contact) {
  return ['Phone', 'MobilePhone', 'Email'].includes(contact.contact_type)
}

function onContactClick(contact) {
  if (contact.contact_type === 'Phone' || contact.contact_type === 'MobilePhone') {
    dialPhone(contact.value)
  } else if (contact.contact_type === 'Email') {
    openEmail(contact.value)
  }
}

function dialPhone(phoneNumber) {
  if (!authStore.hasPermission('dialer.make_calls')) {
    notify.warning('You don\'t have calling permissions. Please contact your administrator.')
    return
  }
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, '')
  window.dispatchEvent(new CustomEvent('dial-phone', {
    detail: { phone_number: cleanNumber, customer_name: customer.value?.name || '' }
  }))
  console.log('[CustomerCardDialog] dial-phone dispatched:', cleanNumber)
}

function openEmail(email) {
  emailTo.value = email
  emailSubject.value = ''
  emailBody.value = ''
  showEmailModal.value = true
  loadOutboundMailboxes()
}

/**
 * REAL email send. Posts to /api/v1/crm/comm/send-email/ with optional
 * from_resource_id / from_email from the company SMTP picker. Omitting From
 * keeps the legacy Client Invoice Email path for the current domain.
 */
async function sendEmail() {
  const subj = (emailSubject.value || '').trim()
  const body = (emailBody.value || '').trim()
  if (!subj) {
    notify.warning('Please enter a subject')
    return
  }
  if (!body) {
    notify.warning('Please enter a message')
    return
  }
  if (!emailTo.value) {
    notify.error('Recipient email is missing')
    return
  }
  emailSending.value = true
  try {
    const mailbox = selectedFromMailbox.value
    await communicationsApi.sendEmail({
      to_email: emailTo.value,
      subject: subj,
      body_text: body,
      customer_id: customer.value?.id,
      customer_name: customer.value?.name || '',
      ...(mailbox?.email
        ? {
            from_email: mailbox.email,
            ...(mailbox.resource_id > 0 ? { from_resource_id: mailbox.resource_id } : {}),
          }
        : {}),
    })
    notify.success('Email sent')
    showEmailModal.value = false
  } catch (err) {
    const detail = err?.response?.data?.error
      || err?.response?.data?.detail
      || err?.message
      || 'Failed to send email'
    console.error('[CustomerCardDialog] sendEmail failed:', err)
    notify.error(`Email failed: ${detail}`)
  } finally {
    emailSending.value = false
  }
}

// Opens the shared SMS dialog. The phone arg is kept for call-site
// compatibility; <SmsDialog> resolves the number(s) from `smsPhones`.
function openSms() {
  showSmsModal.value = true
}

function openMap(address) {
  window.open('https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address), '_blank')
}

function openEdit() {
  showEdit.value = true
}

// Whether the current user may open the full CRM profile page. Mirrors the
// permission the CustomerCard360 route itself enforces (`crm.view`), so we
// never offer a button that would just bounce off the router guard.
const canViewFullProfile = computed(() => authStore.hasPermission('crm.view'))

// The quick card was a dead-end: its only actions were Edit and Close, so a
// user who reached it (e.g. via global search on the Tasks screen) had no way
// to jump to the customer's full record. This opens Customer 360 reactively
// via the router (no page reload) after closing the popup. (MD-4-00052)
function openFullProfile() {
  const id = customer.value?.id
  if (!id) {
    console.warn('[CustomerCardDialog] openFullProfile called with no customer id')
    return
  }
  console.log('[CustomerCardDialog] Opening full profile (Customer 360) for id=', id)
  close()
  router.push({ name: 'CustomerCard360V2', params: { id: String(id) } })
}

function onCustomerUpdated() {
  if (customer.value?.id) {
    open(customer.value.id)
  }
  console.log('[CustomerCardDialog] Customer updated, reloading data')
}

function formatNumber(val) {
  if (val == null) return '0.00'
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const BILL_TO_META = {
  Self:            { label: 'Self pays',         color: 'grey',    icon: 'mdi-account' },
  Landlord:        { label: 'Landlord/Homeowner', color: 'orange', icon: 'mdi-key-chain' },
  PropertyManager: { label: 'Property Manager',  color: 'primary', icon: 'mdi-domain' },
  WarrantyCompany: { label: 'Warranty Company',  color: 'info',    icon: 'mdi-shield-check' },
  Other:           { label: 'Other payer',       color: 'purple',  icon: 'mdi-account-question' },
}

const ROLE_META = {
  tenant:           { label: 'Tenant',           color: 'green',   icon: 'mdi-home-account' },
  landlord:         { label: 'Landlord/Homeowner', color: 'orange', icon: 'mdi-key-chain' },
  property_manager: { label: 'Property Manager', color: 'primary', icon: 'mdi-domain' },
  warranty_company: { label: 'Warranty Company', color: 'info',    icon: 'mdi-shield-check' },
  other:            { label: 'Other',            color: 'grey',    icon: 'mdi-account-multiple' },
}

const billToMeta = computed(() => {
  const t = customer.value?.bill_to_type
  if (!t) return { label: '', color: 'grey', icon: 'mdi-cash' }
  return BILL_TO_META[t] || { label: t, color: 'grey', icon: 'mdi-cash' }
})

const paymentTermsDisplay = computed(() => {
  const c = customer.value
  if (!c) return ''
  if (c.payment_term_name) return c.payment_term_name
  const inherited = c.default_billing_customer_payment_term
  if (inherited?.name) return inherited.name
  return ''
})

const paymentTermsSource = computed(() => {
  const c = customer.value
  if (!c) return null
  // If the customer has its own payment term — no "inherited from" hint.
  if (c.payment_term_name) return null
  // If we fall back to payer's term, show whose term we inherited.
  if (c.default_billing_customer_payment_term?.name) {
    return c.default_billing_customer_name || 'payer'
  }
  return null
})

const roleChips = computed(() => {
  const codes = customer.value?.role_codes || []
  return codes.map(code => ({
    code,
    ...(ROLE_META[code] || { label: code, color: 'grey', icon: 'mdi-account-tag' }),
  }))
})

const hasBillingInfo = computed(() => {
  const c = customer.value
  return !!(c?.bill_to_type || roleChips.value.length || paymentTermsDisplay.value || (c?.pays_for && c.pays_for.length))
})

function goToPayer() {
  const id = customer.value?.default_billing_customer_id
  if (!id) return
  console.log('[CustomerCardDialog] Re-opening dialog for payer id=', id)
  open(id)
}

function goToTenant(id) {
  if (!id) return
  console.log('[CustomerCardDialog] Re-opening dialog for tenant id=', id)
  open(id)
}
</script>

<style scoped>
.customer-card-header {
  background: linear-gradient(135deg, #f8f9fb 0%, #eef1f6 100%);
}

.clickable-link {
  color: #0284c7;
  cursor: pointer;
}

.clickable-link:hover {
  text-decoration: underline;
}

.contact-value {
  line-height: 1.4;
}

.payment-row {
  transition: background-color 0.12s ease;
}
.payment-row:hover {
  background: #f5f7fa;
}

/* Single-line preview of long job summaries; click chevron to expand. */
.job-summary-clamp {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.job-row + .job-row {
  border-top: 1px dashed rgba(0, 0, 0, 0.06);
}
</style>
